// 청구서 납부 과정 > 1. 청구서 납부 방법 선택

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { rootStore } from "@/mobx/store";
import styled from "styled-components";
import { observer } from "mobx-react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";

import fonts from "@/design/typography";
import colors from "@/design/colors";
import { Page_settings } from "@/config/page_settings";
import NaverPayIcon from "@/components/icons/naver_pay";
import KakaoPayIcon from "@/components/icons/kakao_pay";
import { ChevronLeft, ChevronRight } from "@/components/icons/chevron";
import Button from "@/components/button";
import ResetIcon from "@/components/icons/reset";

/* **********************************
 * TYPES
 * 타입 정의
 ********************************** */
type PaymentMethod = "CARD" | "NAVER_PAY" | "KAKAO_PAY" | null;

/* **********************************
 * CONSTANTS
 * 상수 정의
 ********************************** */

const PaymentProcessPage = observer(() => {
  /* **********************************
   * GLOBAL STATE
   * 전역 상태 및 전역 변수
   ********************************** */
  const info = rootStore.getPaymentInfo;

  /* **********************************
   * LOCAL STATE
   * 컴포넌트 내부에서 사용하는 상태
   ********************************** */
  const navigate = useNavigate();

  // 결제 타입
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(null);

  /* **********************************
   * USE EFFECT
   * 컴포넌트 렌더링 시 실행되는 함수
   ********************************** */
  useEffect(() => {
    const info = rootStore.getPaymentInfo;

    console.table({
      method: info?.method,
      cardCompany: info?.cardCompany,
      installment: info?.installment,
      signature: info?.signature,
    });

    if (!info?.method) {
      alert("결제 중 오류가 발생하였습니다. 다시 시도해주세요.");
      navigate("/payments");
      return;
    }
    // if (!info.method) {
    //   alert("결제 중 오류가 발생하였습니다. 다시 시도해주세요.");
    //   navigate("/payments");
    // }

    setPaymentMethod(info.method);
  }, []);

  /* **********************************
   * HANDLERS
   * 사용자 입력 및 이벤트 처리
   ********************************** */

  return (
    <Container>
      <Wrapper>
        {/* 헤더 */}
        <TopBar>
          <BackButton onClick={() => navigate(-1)}>
            <ChevronLeft width="28px" height="28px" />
          </BackButton>
          <TitleText>결제하기</TitleText>
          <Spacer /> {/* 가운데 정렬용 */}
        </TopBar>

        {/* 컨텐츠 */}
        {paymentMethod !== "CARD" ? (
          // 네이버페이 || 카카오페이 결제 시
          <ContentWrapper>
            {/* 네이버페이 결제 시 */}
            {paymentMethod === "NAVER_PAY" ? (
              <>
                <LogoWrapper>
                  <NaverPayIcon width="98px" height="36px" />
                </LogoWrapper>
                <ContentText>네이버페이로 결제하려면</ContentText>
                <ContentText>다음 버튼을 눌러주세요</ContentText>
                <Help>
                  <span>앱이 없으신가요?</span>
                  <HelpButton
                    onClick={() => {
                      const confirmed = window.confirm(
                        "'네이버페이'에서 이 페이지를 열겠습니까?"
                      );
                      if (confirmed) {
                        window.location.href =
                          "https://apps.apple.com/kr/app/%EB%84%A4%EC%9D%B4%EB%B2%84%ED%8E%98%EC%9D%B4/id1554807824";
                      }
                    }}
                  >
                    앱 설치하기
                  </HelpButton>
                </Help>
              </>
            ) : (
              // 카카오페이 결제 시
              <>
                <LogoWrapper>
                  <KakaoPayIcon width="98px" height="36px" />
                </LogoWrapper>
                <ContentText>카카오페이로 결제하려면</ContentText>
                <ContentText>다음 버튼을 눌러주세요</ContentText>
                <Help>
                  <span>앱이 없으신가요?</span>
                  <HelpButton
                    onClick={() => {
                      const confirmed = window.confirm(
                        "'카카오페이'에서 이 페이지를 열겠습니까?"
                      );
                      if (confirmed) {
                        window.location.href =
                          "https://apps.apple.com/kr/app/%EC%B9%B4%EC%B9%B4%EC%98%A4%ED%8E%98%EC%9D%B4/id1464496236";
                      }
                    }}
                  >
                    앱 설치하기
                  </HelpButton>
                </Help>
              </>
            )}
          </ContentWrapper>
        ) : (
          // 카드 결제 시
          <ContentWrapper>앱카드 결제 페이지입니다.</ContentWrapper>
        )}

        <FooterWrapper>
          <Button
            size="large"
            text="다음"
            width="100%"
            height="50px"
            buttonColor={colors.blue600}
            textColor={colors.white}
            onClick={() => {
              navigate("/payments/result");
            }}
          />
        </FooterWrapper>
      </Wrapper>
    </Container>
  );
});

export default PaymentProcessPage;

// 페이지 전체
const Container = styled.div`
  max-width: 600px;
  width: 100%;
  height: 100%;
  background-color: ${colors.gray50};
  box-sizing: border-box;
  margin: 0 auto;
  overflow-y: auto;
`;

// 페이지 전체 > 래퍼
const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${colors.gray50};
`;

const TopBar = styled.div`
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${colors.gray50};
  padding: 0 20px;
`;

const BackButton = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const TitleText = styled.div`
  flex: 1;
  text-align: center;
  font-family: ${fonts.body1SemiBold.fontFamily};
  font-size: ${fonts.body1SemiBold.fontSize};
  line-height: ${fonts.body1SemiBold.lineHeight};
  color: ${colors.gray900};
`;

const Spacer = styled.div`
  width: 28px; /* BackButton과 같은 너비 */
`;

const ContentWrapper = styled.div`
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(
    100vh - 48px - 90px
  ); /* 전체 높이에서 TopBar(48px)와 FooterWrapper(90px) 높이를 뺌 */
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const ContentText = styled.div`
  font-family: ${fonts.heading3SemiBold.fontFamily};
  font-size: ${fonts.heading3SemiBold.fontSize};
  line-height: ${fonts.heading3SemiBold.lineHeight};
  color: ${colors.gray900};
`;

const Help = styled.div`
  font-family: ${fonts.body3Regular.fontFamily};
  font-size: ${fonts.body3Regular.fontSize};
  line-height: ${fonts.body3Regular.lineHeight};
  color: ${colors.gray900};
  margin-top: 40px;

  display: flex;
  align-items: center;
  gap: 12px;
`;

const HelpButton = styled.div`
  cursor: pointer;

  border: 1px solid ${colors.gray100};
  padding: 4px 8px;
  border-radius: 8px;
`;

// 페이지 전체 > 푸터 래퍼
const FooterWrapper = styled.div`
  max-width: 600px;
  background-color: ${colors.white};
  width: 100%;
  position: fixed;
  margin: 0 auto;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px 16px;
  box-sizing: border-box;
`;
